import axios from "axios";
import { toast } from "sonner";
import { API_BASE, TOKEN_KEY } from "./apiConstants";

export { API_BASE, TOKEN_KEY };

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  // Send the HttpOnly refresh-token cookie on every same-origin request.
  // The cookie itself is scoped to Path=/api/auth so it never leaves the
  // auth namespace even with this global default enabled.
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // P1-13 — DPDP consent header. Sent on every API call so the backend
  // can enforce the user's analytics choice server-side. Values:
  //   "1" = granted, "0" = declined, "-" = not yet decided.
  try {
    // Lazy require to avoid a hard dep in edge cases (SSR-like harnesses).
    // eslint-disable-next-line global-require
    const { analyticsConsentHeader } = require("./consent");
    config.headers["X-Analytics-Consent"] = analyticsConsentHeader();
  } catch (_) { /* ignore */ }
  return config;
});

// --- Preview-transient single retry ----------------------------------------
// The preview pod's uvicorn hot-reloader briefly kills the backend socket
// (~500 ms – 2 s window) whenever a file under `/app/backend/` changes, and
// the ingress in front returns HTTP 502 during that window. In-flight
// browser requests surface as either an axios "Network Error" (no response)
// or a plain 502. Sentry captures both. A single short retry masks these
// transients without hiding sustained failures.
//
// Constraints (deliberately narrow to avoid breaking anything else):
//   • Retries at most ONCE per request (guarded by a per-config flag).
//   • Only for idempotent HTTP methods (GET / HEAD / OPTIONS). Never for
//     POST / PUT / PATCH / DELETE — those may have already mutated state
//     server-side when the socket closed.
//   • Only for Axios "Network Error" (no `error.response`) OR HTTP 502.
//     Timeouts (`ECONNABORTED`), 4xx, and other 5xx pass straight through.
//   • Fixed 250 ms backoff — a single wait, no exponential loop.
// Registered BEFORE the toast interceptor so a successful retry silences
// the "Network hiccup" toast that would otherwise flash on the first try.
const TRANSIENT_RETRY_FLAG = "__royalLadyTransientRetried";
const RETRY_IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const config = error?.config;
    if (!config || config[TRANSIENT_RETRY_FLAG]) return Promise.reject(error);
    const method = (config.method || "get").toLowerCase();
    if (!RETRY_IDEMPOTENT_METHODS.has(method)) return Promise.reject(error);
    const isTimeout =
      error?.code === "ECONNABORTED" ||
      (error?.message || "").toLowerCase().includes("timeout");
    const isNetworkError = !error?.response && !isTimeout;
    const is502 = error?.response?.status === 502;
    if (!isNetworkError && !is502) return Promise.reject(error);
    config[TRANSIENT_RETRY_FLAG] = true;
    return new Promise((resolve) => setTimeout(resolve, 250)).then(() =>
      api(config)
    );
  }
);

// Global response interceptor: turn 404 / network errors into friendly toasts
// (never a raw React overlay). Individual callers can still handle errors.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const code = error?.code;

    if (!error.response) {
      // Distinguish: user is offline vs server unreachable vs timeout.
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      const isTimeout = code === "ECONNABORTED" || (error?.message || "").toLowerCase().includes("timeout");

      // Remember the URL the user was on so the branded /offline.html page
      // can restore it once connectivity returns. Silently ignore storage
      // failures (Safari private-mode etc.).
      try {
        if (isOffline && typeof window !== "undefined") {
          localStorage.setItem("royal_lady_offline_target", window.location.pathname + window.location.search);
        }
      } catch (_) { /* ignore */ }

      // Never spam the login / OTP screens — those flows have their own
      // inline error UI.
      if (!url.includes("/auth/")) {
        if (isOffline) {
          toast.error("You're offline — we've saved your progress and will retry automatically.");
        } else if (isTimeout) {
          toast.error("The network is slow right now — please try again in a moment.");
        } else {
          toast.error("Network hiccup — please check your connection.");
        }
      }
    } else if (status === 404) {
      toast.error("That item couldn't be found.");
    } else if (status === 423) {
      // Handled inline by the login form. No toast — the exact message the
      // server returns is shown directly to the user.
    } else if (status >= 500 && status < 600) {
      toast.error("Our servers had a small hiccup. Please try again shortly.");
    } else if (status === 503) {
      toast.error("Royal Lady is briefly unavailable. Please try again in a moment.");
    }
    return Promise.reject(error);
  }
);

// P0-6 · install silent-refresh + multi-tab sync onto the shared axios
// instance. Must run AFTER the response interceptor above so the refresh
// logic sees the raw 401 first.
import { installSessionRefresh } from "./authRefresh";
installSessionRefresh(api);

/**
 * Strip anything that could leak internal implementation details from a
 * user-facing error string: file paths, stack frames, ObjectIds, JWT
 * fragments, sensitive-looking key/value pairs.
 *
 * This is a defence-in-depth net — the backend should already return clean
 * messages, but we never trust that.
 */
function _sanitise(text) {
  if (typeof text !== "string" || !text) return "";
  const banned = [
    /Traceback[\s\S]*$/i,
    /\bat\s+\/?[\w./-]+\.py:\d+/i,
    /\/app\/[\w./-]+/g,
    /Bearer\s+[A-Za-z0-9._-]{10,}/gi,
    /eyJ[A-Za-z0-9._-]{20,}/g,   // JWT fragments
    /[a-f0-9]{24}/gi,             // ObjectIds
  ];
  let out = text;
  for (const rx of banned) out = out.replace(rx, "").trim();
  // Collapse whitespace and cap length so a runaway error can't dominate the UI.
  out = out.replace(/\s+/g, " ").trim();
  if (out.length > 240) out = out.slice(0, 237) + "…";
  return out;
}

export function formatApiError(err, fallback = "Something went wrong") {
  // No HTTP response at all → the browser never reached the server.
  // Return a friendly, brand-appropriate message instead of the raw
  // "Network Error" / "Request aborted" string.
  if (err && !err.response) {
    const code = err.code || "";
    const msg = (err.message || "").toLowerCase();
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "You appear to be offline. Please check your connection and try again.";
    }
    if (code === "ECONNABORTED" || msg.includes("timeout")) {
      return "The network is a bit slow right now. Please try again in a moment.";
    }
    return "We couldn't reach Royal Lady just now. Please try again shortly.";
  }
  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;
  if (!detail) {
    if (status === 401) return "Please sign in and try again.";
    if (status === 403) return "You don't have permission to do that.";
    if (status === 404) return "We couldn't find what you were looking for.";
    if (status === 429) return "Too many attempts — please wait a moment and try again.";
    if (status >= 500) return "Royal Lady is having a moment. Please try again shortly.";
    return _sanitise(err?.message) || fallback;
  }
  if (typeof detail === "string") return _sanitise(detail) || fallback;
  if (Array.isArray(detail)) {
    return _sanitise(
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
        .filter(Boolean)
        .join(", ")
    ) || "Please check the highlighted fields and try again.";
  }
  // Object-shaped detail — never leak keys, always fall back to a friendly line.
  return "Please check the highlighted fields and try again.";
}

export function formatINR(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
